import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Table, Button, Space, message,Tag ,Modal,Spin} from "antd";
import { getRequest, deleteRequest, postRequest } from "../api/api";
import {
  DeleteOutlined,
  LoadingOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { useLocation } from "react-router-dom";
import ChapterSlideModal from "../components/ChapterSlideModal";
import Sortable from "sortablejs";
import DOMPurify from "dompurify";

const ChapterSlides = () => {
  const location = useLocation();
  const passedChapter = location.state?.selectedChapter || null;

  const [chapters, setChapters] = useState([]);
  const [slides, setSlides] = useState([]);
  const [filteredSlides, setFilteredSlides] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState(
    passedChapter?.id || null
  );
  const [selectedChapter, setSelectedChapter] = useState(passedChapter || null);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showModal, setShowModal] = useState(false);
const [viewOpen, setViewOpen] = useState(false);
const [viewSlide, setViewSlide] = useState(null);
const [viewHtml, setViewHtml] = useState("");
const [viewLoading, setViewLoading] = useState(false);


  // Sortable wiring
  const tableBodyRef = useRef(null);
  const sortableRef = useRef(null);
  console.log("setSelectedChapterId", setSelectedChapterId);

  // ----- Effects: fetch -----
  useEffect(() => {
    // fetchChapters();
    // fetchAllSlides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

// ✅ Fetch the .txt and convert it to clean HTML
useEffect(() => {
  const loadHtml = async () => {
    if (!viewOpen || !viewSlide) return;

    const src =
      viewSlide.content || viewSlide.content_html || viewSlide.description || "";

    const isTxtUrl =
      typeof src === "string" &&
      src.startsWith("http") &&
      (src.endsWith(".txt") || src.endsWith(".html"));

    try {
      setViewLoading(true);
      let html = "";

      if (isTxtUrl) {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`Failed to fetch content (${res.status})`);
        const txt = await res.text();

        // 🧹 Clean up the [STEP] markers and trim extra spaces
        html = txt
          .replace(/\[STEP\]/g, "") // remove all [STEP]
          .replace(/\n+/g, "") // remove newlines
          .trim();
      } else {
        html = src;
      }

      setViewHtml(html);
    } catch (err) {
      console.error("Failed to load slide content:", err);
      setViewHtml(
        `<p style="color:#ef4444">Unable to load content. <a href="${src}" target="_blank" rel="noreferrer">Open in new tab</a></p>`
      );
    } finally {
      setViewLoading(false);
    }
  };

  loadHtml();
}, [viewOpen, viewSlide]);



  useEffect(() => {
    if (selectedChapterId && slides.length && chapters.length) {
      const filtered = slides.filter(
        (slide) => slide.chapter_id === selectedChapterId
      );
      setFilteredSlides(filtered);
      const found = chapters.find((ch) => ch.id === selectedChapterId);
      if (found) setSelectedChapter(found);
    } else {
      setFilteredSlides(slides);
    }
  }, [selectedChapterId, slides, chapters]);

  // ----- Data fetchers -----


  // NEW: Fetch slides only for the selected chapter
// ----- Data fetcher (memoized) -----
const fetchSlidesByChapter = useCallback(async (chapterId = selectedChapterId) => {
  if (!chapterId) return;
  setLoading(true);
  try {
    const data = await getRequest(`chapter-slides/chapter/${chapterId}?isChapter=true`);
    setSlides(data);
    setFilteredSlides(data);
  } catch (error) {
    console.error("Error fetching slides:", error);
    message.error("Failed to fetch slides for chapter");
  } finally {
    setLoading(false);
  }
}, [selectedChapterId]);


useEffect(() => {
  if (selectedChapterId) fetchSlidesByChapter();
}, [selectedChapterId, fetchSlidesByChapter]);


  // ----- Helpers -----
const handleDelete = async (record) => {
  const confirm = await Swal.fire({
    title: `Are you sure?`,
    text: `This will permanently delete the slide order: ${record.order}`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it!",
  });

  if (confirm.isConfirmed) {
    try {
      await deleteRequest(`chapter-slides/${record.id}`);
      message.success("Slide deleted successfully");
      await fetchSlidesByChapter(); // ✅ refresh the current chapter’s slides
    } catch {
      message.error("Failed to delete slide");
    }
  }
};


  // DO NOT mutate state in render — memoize a sorted copy
  const sortedSlides = useMemo(() => {
    return [...filteredSlides].sort(
      (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)
    );
  }, [filteredSlides]);

  // Recreate Sortable on the current tbody (no setState here → no loops)
  const initSortable = useCallback(() => {
    // Destroy any previous instance
    if (sortableRef.current) {
      sortableRef.current.destroy();
      sortableRef.current = null;
    }

    const tbody = tableBodyRef.current;
    if (!tbody || sortedSlides.length === 0) return;

    sortableRef.current = Sortable.create(tbody, {
      animation: 150,
      handle: ".drag-handle",
      draggable: "tr.ant-table-row",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      onEnd: async () => {
        // Build new order from DOM
        const rows = Array.from(
          tbody.querySelectorAll("tr.ant-table-row[data-row-key]")
        );
        const orderedIds = rows.map((tr) => tr.getAttribute("data-row-key"));

        const idToSlide = new Map(sortedSlides.map((s) => [String(s.id), s]));
        const newSlidesVisible = orderedIds
          .map((id, idx) => {
            const original = idToSlide.get(String(id));
            return original ? { ...original, order: idx + 1 } : null;
          })
          .filter(Boolean);

        // Update visible list
        setFilteredSlides(newSlidesVisible);

        // Also update the master slides array for the selected chapter
        setSlides((prev) => {
          const updates = new Map(newSlidesVisible.map((s) => [s.id, s.order]));
          return prev.map((s) =>
            s.chapter_id === selectedChapterId && updates.has(s.id)
              ? { ...s, order: updates.get(s.id) }
              : s
          );
        });

        // Persist to API
        try {
          await postRequest("chapter-slides/reorder", {
            slides: newSlidesVisible.map((s) => ({ id: s.id, order: s.order })),
          });
          message.success("Slide order updated");
        } catch {
          message.error("Failed to save order");
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedSlides, selectedChapterId]);

  // Callback ref that re-inits Sortable when AntD swaps <tbody>
  const bodyRefCallback = useCallback(
    (node) => {
      if (node && node !== tableBodyRef.current) {
        tableBodyRef.current = node;
        initSortable(); // imperative — no state update here
      }
    },
    [initSortable]
  );

  // Also re-init when data/flags change (same node but rows/visibility changed)
  useEffect(() => {
    initSortable();
    return () => {
      if (sortableRef.current) {
        sortableRef.current.destroy();
        sortableRef.current = null;
      }
    };
  }, [initSortable, modalOpen, showModal]);

    const openView = (record) => {
  setViewSlide(record);
  setViewOpen(true);
};

  // ----- Columns -----
  const columns = [
    {
      title: "",
      dataIndex: "drag",
      width: 40,
      render: () => (
        <span
          className="drag-handle"
          title="Drag to reorder"
          style={{ cursor: "grab" }}
        >
          <MenuOutlined />
        </span>
      ),
    },
    { title: "Order", dataIndex: "order", width: 80 },
    {
      title: "Image",
      key: "image",
      render: (_, record) => {
        const fallbackImage =
          "/defualt-img.png";
        const imageUrl = record.image || fallbackImage;
        return (
          <img
            src={imageUrl}
            alt="Slide"
            width={50}
            height={50}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = fallbackImage;
            }}
          />
        );
      },
    },
    {
      title: "Image Position",
      dataIndex: "image_position",
      key: "image_position",
      render: (value) => {
        const position = value === "T" ? "Top" : value === "B" ? "Bottom" : "-";
        return <span>{position}</span>;
      },
    },
    {
      title: "Animation",
      dataIndex: "animation_type",
      key: "animation_type",
      render: (val) => <span>{val?.replace(/_/g, " ") || "-"}</span>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (value) => {
        const isActive = value === 1 || value === "1";
        return (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: isActive ? "#10b981" : "#ef4444",
              fontWeight: "500",
            }}
          >
            <span
              style={{
                height: "8px",
                width: "8px",
                borderRadius: "50%",
                backgroundColor: isActive ? "#10b981" : "#ef4444",
                display: "inline-block",
              }}
            />
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space size="middle">
               <Button
        type="default"
        onClick={() => openView(record)}
      >
        View
      </Button>

          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "40px" }}>
      <h2 style={{ fontSize: "24px", fontWeight: "700"  , color: "#5a355a"}}>
        {selectedChapter?.title && ` ${selectedChapter.title}`}
      </h2>

      <div
        style={{
          background: "#fff",
          padding: 24,
          marginTop: 24,
          borderRadius: 8,
          boxShadow: "0 0 4px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div style={{ color: "#64748b", fontSize: 13 }}>
            Tip: Drag <MenuOutlined /> to reorder slides. Changes save
            automatically.
          </div>

          <Button
            type="primary"
            onClick={() => {
              if (!selectedChapterId) {
                message.warning("Please select a chapter before adding slide");
                return;
              }
              const currentOrders = filteredSlides.map((s) =>
                Number(s.order || 0)
              );
              const maxOrder =
                currentOrders.length > 0 ? Math.max(...currentOrders) : 0;
              setEditData({ order: maxOrder + 1 });
              setModalOpen(true);
            }}
            className="add-btn"
          >
            + Add Slide
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={sortedSlides}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          bordered
          loading={{
            spinning: loading,
            indicator: (
              <LoadingOutlined
                style={{ fontSize: 40, color: "#251120" }}
                spin
              />
            ),
          }}
          components={{
            body: {
              // IMPORTANT: do not set state in this ref; just rebind Sortable
              wrapper: (props) => <tbody {...props} ref={bodyRefCallback} />,
            },
          }}
        />
      </div>

      

      <ChapterSlideModal
        visible={modalOpen || showModal}
        chapter={selectedChapter}
        editData={editData}
        maskClosable={false}
        onClose={() => {
          setModalOpen(false);
          setShowModal(false);
          setEditData(null);
        }}
        onSuccess={async  () => {
         await fetchSlidesByChapter();
          setModalOpen(false);
          setShowModal(false);
          setEditData(null);
        }}
      />


<Modal
  open={viewOpen}
  onCancel={() => {
    setViewOpen(false);
    setViewHtml("");
  }}
  footer={null}
  width={840}
  title={
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span>Slide Details</span>
      {viewSlide?.order != null && (
        <Tag color="geekblue">Order: {viewSlide.order}</Tag>
      )}
      {viewSlide?.is_active != null && (
        <Tag color={Number(viewSlide.is_active) ? "green" : "red"}>
          {Number(viewSlide.is_active) ? "Active" : "Inactive"}
        </Tag>
      )}
    </div>
  }
>
  {viewSlide && (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Image + meta */}
      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <img
          src={viewSlide.image || "/defualt-img.png"}
          alt="Slide"
          width={120}
          height={120}
          style={{
            objectFit: "cover",
            borderRadius: 8,
            border: "1px solid #eee",
          }}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/defualt-img.png";
          }}
        />
        <div style={{ color: "#666" }}>
          <div>
            <strong>Image Position:</strong>{" "}
            {viewSlide.image_position === "T"
              ? "Top"
              : viewSlide.image_position === "B"
              ? "Bottom"
              : "-"}
          </div>
          <div>
            <strong>Animation:</strong>{" "}
            {viewSlide.animation_type?.replace(/_/g, " ") || "-"}
          </div>
        </div>
      </div>

      {/* Render Fetched HTML */}
      <div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Content</div>
        <div
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 16,
            background: "#fafafa",
            maxHeight: 480,
            overflow: "auto",
            position: "relative",
          }}
        >
          {viewLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "40px 0",
              }}
            >
              <Spin />
            </div>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(viewHtml || ""),
              }}
            />
          )}
        </div>
      </div>
    </div>
  )}
</Modal>;


    </div>

    
  );
};

export default ChapterSlides;
